import { createRouter, createWebHistory } from 'vue-router'
// Documentation: https://router.vuejs.org/guide/migration/

import withRoutes from '../routes'
import { hasNewVersion } from '~/shared/version'

/**
 * Takes an optional state Object and returns an object with `state`,
 * `add`, `get` and `clear` keys for adding, accessing and clearing
 * savedPositions within that state Object
 *
 * @param {Object} state optional state Object to store savedPositions in
 */
export const savedPositionsWithState = (state = {}) => ({
  /** readonly state object */
  get state () { return state },
  /** get stored scroll position by routeName and then clear it  */
  get (routeName) {
    const position = this.state[routeName]
    if (!position) return
    this.clear(routeName)
    return position
  },
  /** store current scroll position by routeName */
  add (routeName) { state[routeName] = { x: 0, y: window.scrollY } },
  /** clear stored scroll position by routeName */
  clear (routeName) { delete state[routeName] }
})

export const router = withRoutes(createRouter({
  history: createWebHistory(),
  routes: [],
  scrollBehavior (to, from, savedPosition) {
    if (to.name !== from.name) {
      const scrollPosition = router.savedPositions.get(to.name)
      if (scrollPosition) {
        return { left: scrollPosition.x, top: scrollPosition.y }
      }
      return savedPosition || { left: 0, top: 0 }
    }
  }
}))

router.savedPositions = savedPositionsWithState()

router.beforeEach((to, from, next) => {
  if (top !== self && to.meta?.layout !== 'none') top.location.replace(top.location.href)
  document.title = [to.meta.title, 'ACV MAX Inventory Management'].filter(Boolean).join(' - ')

  const isFullscreen = to.meta.fullscreen
  document.documentElement.classList[isFullscreen ? 'add' : 'remove']('layout--fullscreen')

  next()
})

router.onError(async ({ message }, to) => {
  if (
    !message.includes('Unable to preload CSS') &&
    !message.includes('Failed to fetch dynamically imported module') &&
    !message.includes('Failed to load module script')
  ) return
  if (!await hasNewVersion()) return

  window.history.pushState(null, null, window.location.origin + to.fullPath)
  window.location.reload(true)
})
